import { useTradingDocument } from "api/trading-documents/hooks";
import { CommonError, DisabledOpacity, MockupLoader } from "components/utils";
import { DrawerRightPanel, RightPanelBody } from "components/utils/drawer";
import { useQuery } from "hooks";
import { AmountSection } from "pages/tradingDocuments/shared/components/rightPanel/amountSection/AmountSection";
import { CorrectionSection } from "pages/tradingDocuments/shared/components/rightPanel/correctionSection/CorrectionSection";
import { CorrespondingDocumentSection } from "pages/tradingDocuments/shared/components/rightPanel/correspondingDocumentsSection/CorrespondingDocumentSection";
import { GeneralInfoSection } from "pages/tradingDocuments/shared/components/rightPanel/generalInfoSection/GeneralInfoSection";
import { ItemsSummarySection } from "pages/tradingDocuments/shared/components/rightPanel/itemsSummarySection/ItemsSummarySection";
import { NotificationSection } from "pages/tradingDocuments/shared/components/rightPanel/notificationSection/NotificationSection";
import { PaymentSection } from "pages/tradingDocuments/shared/components/rightPanel/paymentSection/PaymentSection";
import { TitleSection } from "pages/tradingDocuments/shared/components/rightPanel/titleSection/TitleSection";
import { PanelHeader } from "./components/PanelHeader";
import { CommentsGenericSection } from "components/common/commentsGeneric/CommentsGenericSection";
import { SellerInfoSection } from "pages/tradingDocuments/shared/components/rightPanel/sellerInfoSection/SellerInfoSection";
import { useEffect, useRef, useState } from "react";

interface Props {
  close: () => void;
}

export const RightPanel = ({ close }: Props) => {
  const { query } = useQuery();
  const { panelId } = query;
  const [isScrolledOver, setIsScrolledOver] = useState(false);
  const clearCommentsRef = useRef<HTMLDivElement>(null);
  const { data: receipt, error, isLoading } = useTradingDocument(
    { id: panelId },
    { enabled: Boolean(panelId) },
  );

  useEffect(() => {
    setIsScrolledOver(false);
  }, [panelId]);

  if (error) {
    return (
      <DrawerRightPanel>
        <CommonError status={error._httpStatus_} />
      </DrawerRightPanel>
    );
  }

  if (isLoading) {
    return (
      <DrawerRightPanel>
        <MockupLoader type="drawer" />
      </DrawerRightPanel>
    );
  }

  if (!receipt) return null;

  return (
    <DrawerRightPanel key={receipt.id}>
      <PanelHeader close={close} isScrolledOver={isScrolledOver} />
      <DisabledOpacity disabled={isLoading}>
        <RightPanelBody
          myRef={clearCommentsRef}
          setIsScrolledOver={setIsScrolledOver}
          hasCommentsModule
        >
          <TitleSection tradingDocument={receipt} />
          <GeneralInfoSection tradingDocument={receipt} />
          <AmountSection tradingDocument={receipt} />
          {receipt.items.length > 0 && <ItemsSummarySection tradingDocument={receipt} />}
          <PaymentSection tradingDocument={receipt} />
          <NotificationSection tradingDocument={receipt} />
          {receipt.corrections.length > 0 && <CorrectionSection tradingDocument={receipt} />}
          {receipt.correspondingDocuments.length > 0 && (
            <CorrespondingDocumentSection tradingDocument={receipt} />
          )}
          <SellerInfoSection tradingDocument={receipt} />
        </RightPanelBody>
        <CommentsGenericSection
          outsideRef={clearCommentsRef}
          paramName="tradingDocument"
          endpointUrl="/finances/comments/items"
          commentedObject={receipt}
        />
      </DisabledOpacity>
    </DrawerRightPanel>
  );
};
